import React from 'react';
import { Link } from 'react-router-dom';
import { DocsLayout } from '../components/DocsLayout';
import { MarkdownContent } from '../components/MarkdownContent';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  MapPin,
  List,
  Filter,
  Route,
  Navigation,
  Moon,
  Megaphone,
  Building2,
  Users,
  UserCircle
} from 'lucide-react';

export const FeaturesIndexPage: React.FC = () => {
  const features = [
    {
      icon: MapPin,
      title: 'Recall Map',
      description:
        'Interactive geographic visualization of vehicle recalls across your dealer network',
      link: '/docs/features/recall-map',
      capabilities: [
        'Real-time map clustering',
        'Heat map visualization',
        'Dealer location filtering',
        'Custom boundary drawing'
      ]
    },
    {
      icon: List,
      title: 'Recall List & Search',
      description: 'Comprehensive searchable list of all recalls with advanced filtering',
      link: '/docs/features/recall-list',
      capabilities: [
        'Quick search by VIN, customer name, phone',
        'Advanced column filtering',
        'Sort by any field',
        'Export to CSV'
      ]
    },
    {
      icon: Filter,
      title: 'Filters System',
      description: 'Powerful multi-criteria filtering to find exactly what you need',
      link: '/docs/features/filters',
      capabilities: [
        'Campaign filtering',
        'Dealer and location filtering',
        'Date range filtering',
        'Status and completion filtering'
      ]
    },
    {
      icon: Route,
      title: 'Manual Routing',
      description: 'Create optimized routes for mobile recall services',
      link: '/docs/features/manual-routing',
      capabilities: [
        'AI-powered route optimization',
        'Technician assignment',
        'Multi-stop route planning',
        'Route preview and editing'
      ]
    },
    {
      icon: Navigation,
      title: 'Route Tracker',
      description: 'Monitor active routes and track technician progress in real-time',
      link: '/docs/features/route-tracker',
      capabilities: [
        'Live route status',
        'Completion tracking',
        'Performance metrics',
        'Route history'
      ]
    },
    {
      icon: Moon,
      title: 'Dark Mode',
      description: 'Eye-friendly dark theme with system preference sync',
      link: '/docs/features/dark-mode',
      capabilities: [
        'Toggle light/dark modes',
        'Sync with system preference',
        'Persistent preference',
        'All pages supported'
      ]
    },
    {
      icon: Megaphone,
      title: 'Campaign Management',
      description: 'Configure and prioritize recall campaigns',
      link: '/docs/features/campaigns',
      capabilities: [
        'Campaign creation',
        'Priority settings',
        'Mobile eligibility rules',
        'Campaign analytics'
      ]
    },
    {
      icon: Building2,
      title: 'Dealer Management',
      description: 'Manage dealer locations and service areas',
      link: '/docs/features/dealers',
      capabilities: [
        'Add/edit dealers',
        'Service area configuration',
        'Location grouping',
        'Dealer analytics'
      ]
    },
    {
      icon: Users,
      title: 'Technician Management',
      description: 'Manage technicians and their assignments',
      link: '/docs/features/technicians',
      capabilities: [
        'Technician profiles',
        'Skill and certification tracking',
        'Availability management',
        'Performance tracking'
      ]
    },
    {
      icon: UserCircle,
      title: 'Profile & Settings',
      description: 'Customize your account and application preferences',
      link: '/docs/features/profile',
      capabilities: [
        'Update profile information',
        'Notification preferences',
        'Display preferences',
        'Security settings'
      ]
    }
  ];

  return (
    <DocsLayout
      title='Features Overview'
      description='Explore all the powerful features of the Vehix recall intelligence platform'
      breadcrumbs={[{ label: 'Documentation', path: '/docs' }, { label: 'Features' }]}
    >
      <div className='max-w-[1000px]'>
        <MarkdownContent>
          <div className='not-prose'>
            <p className='text-sm leading-6 text-muted-foreground mb-8'>
              Vehix provides a comprehensive suite of tools designed to streamline recall
              management, optimize mobile service routing, and improve customer satisfaction.
              Explore each feature below to learn more.
            </p>

            <div className='grid gap-6 md:grid-cols-2 mb-12'>
              {features.map(feature => {
                const Icon = feature.icon;
                return (
                  <Link key={feature.title} to={feature.link} className='no-underline'>
                    <Card className='h-full hover:border-primary transition-colors cursor-pointer'>
                      <CardHeader>
                        <div className='flex items-center gap-3 mb-2'>
                          <div className='p-2 bg-primary/10 rounded-lg'>
                            <Icon className='h-8 w-8 text-primary' />
                          </div>
                          <CardTitle className='text-lg font-semibold'>{feature.title}</CardTitle>
                        </div>
                        <CardDescription className='text-sm leading-6'>
                          {feature.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <ul className='space-y-1 text-sm text-muted-foreground'>
                          {feature.capabilities.map(capability => (
                            <li key={capability} className='flex items-start gap-2'>
                              <span className='text-primary mt-0.5'>•</span>
                              <span>{capability}</span>
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Role-Based Features</h2>
            <p className='text-sm leading-6'>
              Different features are available based on your assigned role. Here's a quick overview
              of which roles have access to which features:
            </p>

            <div className='not-prose my-6'>
              <div className='overflow-x-auto'>
                <table className='w-full border-collapse border border-border text-sm'>
                  <thead>
                    <tr className='bg-muted'>
                      <th className='border border-border px-4 py-2 text-left font-semibold'>
                        Feature
                      </th>
                      <th className='border border-border px-4 py-2 text-left font-semibold'>
                        Technician
                      </th>
                      <th className='border border-border px-4 py-2 text-left font-semibold'>
                        Service Advisor
                      </th>
                      <th className='border border-border px-4 py-2 text-left font-semibold'>
                        Service Manager
                      </th>
                      <th className='border border-border px-4 py-2 text-left font-semibold'>
                        Admin Roles
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className='border border-border px-4 py-2'>Recall Map (View)</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                    </tr>
                    <tr className='bg-muted/50'>
                      <td className='border border-border px-4 py-2'>Route Tracker</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                    </tr>
                    <tr>
                      <td className='border border-border px-4 py-2'>Manual Routing</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                    </tr>
                    <tr className='bg-muted/50'>
                      <td className='border border-border px-4 py-2'>Campaign Management</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                    </tr>
                    <tr>
                      <td className='border border-border px-4 py-2'>Dealer Management</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                    </tr>
                    <tr className='bg-muted/50'>
                      <td className='border border-border px-4 py-2'>Technician Management</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>-</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                      <td className='border border-border px-4 py-2 text-center'>✓</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Getting Started</h2>
            <p className='text-sm leading-6'>
              New to Vehix? We recommend starting with the{' '}
              <Link to='/docs/getting-started'>Getting Started Guide</Link> to learn the basics,
              then exploring the features most relevant to your role.
            </p>
          </section>

          <section className='mb-20'>
            <h2 className='text-3xl font-bold mb-6'>Need Help?</h2>
            <p className='text-sm leading-6'>
              If you have questions about any feature, visit our{' '}
              <Link to='/docs/support'>Support page</Link> or check out our{' '}
              <Link to='/docs/guides'>How-To Guides</Link> for step-by-step instructions.
            </p>
          </section>
        </MarkdownContent>
      </div>
    </DocsLayout>
  );
};
